/**
 * Copyright 2026 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import React from "react";
import {
  Box,
  Button,
  Divider,
  FormHelperText,
  Grid2,
  InputLabel,
  MenuItem
} from "@mui/material";
import { useFormikContext, getIn } from "formik";
import T from "i18n-react/dist/i18n-react";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import MetaFieldValues from "./meta-field-values";
import MuiFormikTextField from "../mui-formik-textfield";
import MuiFormikSelect from "../mui-formik-select";
import MuiFormikCheckbox from "../mui-formik-checkbox";
import {
  METAFIELD_TYPES,
  METAFIELD_TYPES_WITH_OPTIONS
} from "../../../../utils/constants";

const AdditionalInput = ({
  item,
  itemIdx,
  baseName,
  onAdd,
  onDelete,
  onDeleteValue,
  entityId,
  isAddDisabled
}) => {
  const { errors, touched, values, setFieldValue } = useFormikContext();

  const buildFieldName = (fieldName) => `${baseName}[${itemIdx}].${fieldName}`;
  const currentType = getIn(values, buildFieldName("type"));

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setFieldValue(buildFieldName("type"), newType);
    if (!METAFIELD_TYPES_WITH_OPTIONS.includes(newType)) {
      setFieldValue(buildFieldName("values"), []);
    }
  };

  const fieldErrors = getIn(errors, `${baseName}[${itemIdx}]`);
  const fieldTouched = getIn(touched, `${baseName}[${itemIdx}]`);

  const showValuesError =
    fieldTouched?.values &&
    fieldErrors?.values &&
    typeof fieldErrors.values === "string";

  return (
    <Grid2 container spacing={2} sx={{ alignItems: "center" }}>
      <Grid2 size={11}>
        <Box
          sx={{
            border: "1px solid #0000001F",
            borderRadius: "4px",
            p: 2,
            my: 2
          }}
        >
          <Grid2 container spacing={2} sx={{ alignItems: "start" }}>
            <Grid2 size={4}>
              <InputLabel htmlFor={buildFieldName("name")}>
                {T.translate("additional_inputs.title")}
              </InputLabel>
              <MuiFormikTextField
                name={buildFieldName("name")}
                margin="none"
                placeholder={T.translate(
                  "additional_inputs.placeholders.title"
                )}
                fullWidth
              />
            </Grid2>
            <Grid2 size={4}>
              <InputLabel htmlFor={buildFieldName("type")}>
                {T.translate("additional_inputs.type")}
              </InputLabel>
              <MuiFormikSelect
                name={buildFieldName("type")}
                placeholder={T.translate(
                  "additional_inputs.placeholders.type"
                )}
                onChange={handleTypeChange}
              >
                {METAFIELD_TYPES.map((fieldType) => (
                  <MenuItem key={fieldType} value={fieldType}>
                    {fieldType}
                  </MenuItem>
                ))}
              </MuiFormikSelect>
            </Grid2>
            <Grid2 size={4} sx={{ alignSelf: "end" }}>
              <MuiFormikCheckbox
                name={buildFieldName("is_required")}
                label={T.translate("additional_inputs.required")}
              />
            </Grid2>
          </Grid2>
          {METAFIELD_TYPES_WITH_OPTIONS.includes(currentType) && (
            <>
              <Divider sx={{ mt: 2 }} />
              <MetaFieldValues
                field={item}
                fieldIndex={itemIdx}
                baseName={baseName}
                onMetaFieldTypeValueDeleted={onDeleteValue}
                entityId={entityId}
              />
              {showValuesError && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {fieldErrors.values}
                </FormHelperText>
              )}
            </>
          )}
          {currentType === "Quantity" && (
            <Grid2 container spacing={2} sx={{ alignItems: "start", mt: 2 }}>
              <Grid2 size={4}>
                <MuiFormikTextField
                  id={buildFieldName("minimum_quantity")}
                  name={buildFieldName("minimum_quantity")}
                  type="number"
                  margin="none"
                  fullWidth
                  label={T.translate("additional_inputs.minimum")}
                  placeholder={T.translate("additional_inputs.placeholders.minimum_quantity")}
                />
              </Grid2>
              <Grid2 size={4}>
                <MuiFormikTextField
                  id={buildFieldName("maximum_quantity")}
                  name={buildFieldName("maximum_quantity")}
                  type="number"
                  margin="none"
                  fullWidth
                  label={T.translate("additional_inputs.maximum")}
                  placeholder={T.translate("additional_inputs.placeholders.maximum_quantity")}
                />
              </Grid2>
            </Grid2>
          )}
        </Box>
      </Grid2>
      <Grid2 size={1}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1
          }}
        >
          <Button
            variant="outlined"
            aria-label="delete"
            sx={{
              width: 40,
              height: 40,
              minWidth: "auto",
              borderRadius: "50%",
              padding: 0
            }}
            onClick={() => onDelete(item, itemIdx)}
          >
            <DeleteIcon />
          </Button>
          <Button
            variant="contained"
            aria-label="add"
            disabled={isAddDisabled}
            sx={{
              width: 40,
              height: 40,
              minWidth: "auto",
              borderRadius: "50%",
              padding: 0
            }}
            onClick={onAdd}
          >
            <AddIcon />
          </Button>
        </Box>
      </Grid2>
    </Grid2>
  );
};

export default AdditionalInput;
